import React from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DateRange } from "@/types/enums";
import {
  getDateRange
  //extractNumericValueFromString
} from "@/lib/utils/date";
//import { SummaryCard } from "@/components/dashboard/SummaryCard";
import { useDashboardAnalytics } from "@/contexts/DashboardAnalyticsContext";
import { Loader } from "@/components/common/Loader";
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue
// } from "@/components/ui/select";
import { useCopilotReadable } from "@copilotkit/react-core";
import { LastCampaignROI } from "@/components/dashboard/LastCampaignROI";
// import { SalesPerformanceChart } from "@/components/dashboard/charts/SalesPerformanceChart";
// import { CustomerGrowthChart } from "@/components/dashboard/charts/CustomerGrowthChart";
import { OnboardingBanner } from "@/components/dashboard/OnboardingBanner";
import { OnboardingSidePanel } from "@/components/onboarding/OnboardingSidePanel";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { useLocation } from "react-router";
import { usePermissions } from "@/hooks/usePermissions";
import FeatureUtils from "@/utility/FeatureUtils";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { SalesCard } from "@/components/dashboard/SalesCard";
import { CustomersCard } from "@/components/dashboard/CustomersCard";
import { TopProductCard } from "@/components/dashboard/TopProductCard";
import { DidYouKnowCard } from "@/components/dashboard/DidYouKnowCard";
import { AskEmmaCard } from "@/components/dashboard/AskEmmaCard";
import { SMSCampaignCard } from "@/components/dashboard/SMSCampaignCard";
import { IndustryInsightsCard } from "@/components/dashboard/IndustryInsightsCard";
import { PromotionalBanner } from "@/components/layout/PromotionalBanner";
import { useBanner } from "@/hooks/useBanner";

export default function Dashboard() {
  useOnboardingProgress();

  const { showOnboardingFlow, onboardingProgress } = useOrganizationStore();
  const { permissions } = usePermissions();
  const { activeBanners, dismissBanner } = useBanner();

  //const [isEmmaChatOpen, setIsEmmaChatOpen] = useState(false);

  // Check appType using FeatureUtils methods
  const isCRM = FeatureUtils.isCRMEnabled(permissions);
  const isAnalytics = FeatureUtils.isAnalyticsEnabled(permissions);

  let appType: "CRM" | "ANALYTICS" = "CRM";
  if (isAnalytics) {
    appType = "ANALYTICS";
  } else if (isCRM) {
    appType = "CRM";
  }

  const location = useLocation();
  const [isSidePanelOpen, setIsSidePanelOpen] = React.useState(false);

  // Helper function to check if onboarding is complete based on app type
  const isOnboardingComplete = (
    onboardingProgress: any,
    appType: "CRM" | "ANALYTICS"
  ): boolean => {
    if (!onboardingProgress) return false;

    if (appType === "CRM") {
      // CRM has 7 tasks
      const crmTasks = [
        onboardingProgress.customer,
        onboardingProgress.customersReportOverview,
        onboardingProgress.smsCampaigns,
        onboardingProgress.productCatalog,
        onboardingProgress.transactions,
        onboardingProgress.salesReportOverview,
        onboardingProgress.automatedCampaigns
      ];
      return crmTasks.every((status) => status === "DONE");
    } else if (appType === "ANALYTICS") {
      // Analytics has 6 tasks
      const analyticsTasks = [
        onboardingProgress.customer,
        onboardingProgress.behaviorAndEngagementReport,
        onboardingProgress.purchasePatternReport,
        onboardingProgress.salesReportOverview,
        onboardingProgress.trendsAndPatternsReport,
        onboardingProgress.productInsightsReport
      ];
      return analyticsTasks.every((status) => status === "DONE");
    }

    return false;
  };

  // Handle onboarding completion and panel visibility
  React.useEffect(() => {
    if (!onboardingProgress || !showOnboardingFlow) return;

    const isComplete = isOnboardingComplete(onboardingProgress, appType);
    const manuallyClosed =
      localStorage.getItem("onboarding-panel-closed") === "true";
    const wasOpen = localStorage.getItem("onboarding-panel-is-open") === "true";

    if (location.pathname === "/") {
      // If onboarding is complete, show panel for completion timer
      if (isComplete) {
        if (!manuallyClosed && (wasOpen || !isSidePanelOpen)) {
          setIsSidePanelOpen(true);
        }
        // Don't auto-hide when complete - let the completion timer handle it
      } else {
        // If onboarding is not complete, show panel normally
        if (!manuallyClosed && (wasOpen || !isSidePanelOpen)) {
          setIsSidePanelOpen(true);
        }
      }
    }
  }, [
    location.pathname,
    onboardingProgress,
    showOnboardingFlow,
    isSidePanelOpen
  ]);

  React.useEffect(() => {
    if (isSidePanelOpen) {
      localStorage.setItem("onboarding-panel-is-open", "true");
    } else {
      localStorage.removeItem("onboarding-panel-is-open");
    }
  }, [isSidePanelOpen]);

  const handleStartGrowing = () => {
    setIsSidePanelOpen(true);
    localStorage.removeItem("onboarding-panel-closed");
  };

  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
    localStorage.setItem("onboarding-panel-closed", "true");
  };
  // const [selectedFilter, setSelectedFilter] = React.useState<DateRange>(
  //   DateRange.YESTERDAY
  // );
  const selectedFilter = DateRange.YESTERDAY;
  //const [selectedRange, setSelectedRange] = React.useState<number>(1);
  const selectedRange = 1;
  // const [queryParams, setQueryParams] = React.useState(() => {
  //   const dates = getDateRange(DateRange.YESTERDAY);
  //   return {
  //     fromDate: dates.startDate,
  //     toDate: dates.endDate,
  //     compareFromDate: dates.compareStartDate,
  //     compareToDate: dates.compareEndDate
  //   };
  // });
  const queryParams = {
    fromDate: getDateRange(selectedFilter).startDate,
    toDate: getDateRange(selectedFilter).endDate,
    compareFromDate: getDateRange(selectedFilter).compareStartDate,
    compareToDate: getDateRange(selectedFilter).compareEndDate
  };

  const {
    sales,
    transactions,
    newCustomers,
    repeatCustomers,
    isLoading
    // salesBucket,
    // customersBucket
  } = useDashboardAnalytics(queryParams, false);

  // const handleFilterChange = (value: string) => {
  //   const filter = value as DateRange;
  //   setSelectedFilter(filter);

  //   if (filter === DateRange.ALL_TIME) {
  //     setQueryParams({
  //       fromDate: "",
  //       toDate: "",
  //       compareFromDate: "",
  //       compareToDate: ""
  //     });
  //     setSelectedRange(0);
  //   } else {
  //     const selectedDateRange = getDateRange(filter);
  //     const numericValue = extractNumericValueFromString(filter);
  //     setQueryParams({
  //       fromDate: selectedDateRange.startDate,
  //       toDate: selectedDateRange.endDate,
  //       compareFromDate: selectedDateRange.compareStartDate,
  //       compareToDate: selectedDateRange.compareEndDate
  //     });
  //     setSelectedRange(numericValue);
  //   }
  // };

  useCopilotReadable({
    description: `New customers of ${selectedRange} days`,
    value: newCustomers?.count || 0,
    categories: ["New Customers"]
  });
  useCopilotReadable({
    description: `Sales of ${selectedRange} days`,
    value: sales?.sum || 0,
    categories: ["Sales"]
  });
  useCopilotReadable({
    description: `Return customers of ${selectedRange} days`,
    value: repeatCustomers?.count || 0,
    categories: ["Return Customers"]
  });
  useCopilotReadable({
    description: `Transaction count of ${selectedRange} days`,
    value: transactions?.count || 0,
    categories: ["Transactions"]
  });

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="p-4 sm:p-6 space-y-2 sm:space-y-6">
      {/* Promotional Banner */}
      {activeBanners.length > 0 && (
        <div className="w-full bg-white border-b border-gray-200">
          <PromotionalBanner
            banners={activeBanners}
            onDismiss={dismissBanner}
            className="max-w-full"
            autoSlide={true}
            slideInterval={5000}
          />
        </div>
      )}
      <OnboardingBanner onStartGrowing={handleStartGrowing} />
      <OnboardingSidePanel
        isOpen={isSidePanelOpen}
        onClose={handleCloseSidePanel}
      />

      <Alert>
        <AlertDescription>
          Your dashboard highlights the past 3 months for streamlined,
          actionable insights.
        </AlertDescription>
      </Alert>

      <div className="mt-16">
        <DashboardHeader />

        {/* Row 1: Daily Business Overview */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-6">
          <SalesCard
            count={sales?.sum || 0}
            percentageValue={sales?.comparisonPercentage || 0}
          />
          <CustomersCard
            count={newCustomers?.count || 0}
            percentageValue={newCustomers?.comparisonPercentage || 0}
          />
          <TopProductCard />
          <DidYouKnowCard />
        </div>

        {/* Ask Emma - Full Width */}
        <div className="mb-6 animate-fade-in">
          <AskEmmaCard
            // onOpenChat={() => setIsEmmaChatOpen(true)}
            onOpenChat={() => {
              // Open the vertical right-side AI chat (Copilot) via a global event
              window.dispatchEvent(new window.Event("openEmmaChat"));
            }}
          />
        </div>

        {/* Row 2: Campaigns & Insights */}
        <div className="grid gap-6 md:grid-cols-2">
          <SMSCampaignCard />
          <IndustryInsightsCard />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* <SummaryCard
          title="New Customers"
          id="new_customers"
          count={newCustomers?.count || 0}
          percentageValue={newCustomers?.comparisonPercentage || 0}
          dateRange={selectedRange}
          isAllowDecimal
        /> */}
        {/* <SummaryCard
          title="Sales"
          id="sales"
          count={sales?.sum || 0}
          percentageValue={sales?.comparisonPercentage || 0}
          dateRange={selectedRange}
          isAllowDecimal
        /> */}
        {/* <SummaryCard
          title="Return Customers"
          id="return_customers"
          count={repeatCustomers?.count || 0}
          percentageValue={repeatCustomers?.comparisonPercentage || 0}
          dateRange={selectedRange}
          isAllowDecimal
        /> */}
        {/* <SummaryCard
          title="Transaction Count"
          id="transactions"
          count={transactions?.count || 0}
          percentageValue={transactions?.comparisonPercentage || 0}
          dateRange={selectedRange}
          isAllowDecimal
        /> */}
      </div>

      {/* <div className="flex justify-end mb-4 sm:mb-6">
        <Select value={selectedFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[160px] sm:w-[180px]">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            {Object.values(DateRange).map((range) => (
              <SelectItem key={range} value={range}>
                {range}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <SalesPerformanceChart
          chartData={salesBucket || []}
          isLoading={isLoading}
        />
        <CustomerGrowthChart
          chartData={customersBucket || []}
          isLoading={isLoading}
        />
      </div> */}

      <div className="mt-8">
        <LastCampaignROI />
      </div>
    </div>
  );
}
